import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Modal } from '../../components/ui/Modal.jsx';
import { EmptyState } from '../../components/ui/EmptyState.jsx';
import { Skeleton } from '../../components/ui/Skeleton.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { productionApi } from '../../services/productionApi.js';
import { formatCurrency } from '../../utils/formatters.js';

function WorkCenterModal({ open, onClose, onCreate }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ code: '', name: '', description: '', hourlyRate: '', capacityPerDay: '' });
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    onCreate({
      code: form.code,
      name: form.name,
      description: form.description || null,
      hourlyRate: form.hourlyRate || 0,
      capacityPerDay: form.capacityPerDay || 0,
    });
  };

  return (
    <Modal open={open} onClose={onClose} title={t('production.centers.new')}>
      <form onSubmit={submit} className="form-stack">
        <div className="field-row">
          <div className="field">
            <label className="field-label" htmlFor="wc-code">{t('production.centers.code')}</label>
            <input id="wc-code" className="input" required value={form.code} onChange={set('code')} />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="wc-name">{t('production.centers.name')}</label>
            <input id="wc-name" className="input" required value={form.name} onChange={set('name')} />
          </div>
        </div>
        <div className="field">
          <label className="field-label" htmlFor="wc-desc">{t('production.centers.description')}</label>
          <input id="wc-desc" className="input" value={form.description} onChange={set('description')} />
        </div>
        <div className="field-row">
          <div className="field">
            <label className="field-label" htmlFor="wc-rate">{t('production.centers.hourlyRate')}</label>
            <input id="wc-rate" type="number" min="0" step="0.01" className="input" value={form.hourlyRate} onChange={set('hourlyRate')} />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="wc-cap">{t('production.centers.capacity')}</label>
            <input id="wc-cap" type="number" min="0" step="0.01" className="input" value={form.capacityPerDay} onChange={set('capacityPerDay')} />
          </div>
        </div>
        <div className="page-actions">
          <button type="button" className="btn" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" className="btn primary">{t('common.create')}</button>
        </div>
      </form>
    </Modal>
  );
}

export function WorkCentersPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);

  const centers = useQuery({
    queryKey: ['production', 'work-centers'],
    queryFn: () => productionApi.listWorkCenters().then((r) => r.items),
  });

  const createMutation = useMutation({
    mutationFn: (body) => productionApi.createWorkCenter(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production', 'work-centers'] });
      setModalOpen(false);
    },
  });

  const rows = centers.data || [];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('production.centers.title')}</h1>
          <p className="muted">{t('production.centers.subtitle')}</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn primary" onClick={() => setModalOpen(true)}>
            {t('production.centers.new')}
          </button>
        </div>
      </header>

      {centers.isLoading ? (
        <Skeleton variant="rows" count={5} />
      ) : rows.length === 0 ? (
        <EmptyState message={t('production.centers.empty')} />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>{t('production.centers.code')}</th>
              <th>{t('production.centers.name')}</th>
              <th className="num">{t('production.centers.hourlyRate')}</th>
              <th className="num">{t('production.centers.capacity')}</th>
              <th>{t('common.status')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((wc) => (
              <tr key={wc.id}>
                <td className="mono">{wc.code}</td>
                <td>{wc.name}</td>
                <td className="num">{formatCurrency(wc.hourlyRate)}</td>
                <td className="num">{wc.capacityPerDay}</td>
                <td>
                  <span className={`status-pill status-${wc.isActive ? 'active' : 'inactive'}`}>
                    {wc.isActive ? t('common.active') : t('common.inactive')}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <WorkCenterModal open={modalOpen} onClose={() => setModalOpen(false)} onCreate={(body) => createMutation.mutate(body)} />
    </section>
  );
}
